import {
  Box,
  Grid,
  Typography,
  makeStyles,
  Theme,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import { observer } from 'mobx-react';
import { useState, useEffect } from 'react';
import { CustomTaskDialog, useIsUploadLocked } from '@roc/feature-general-tasks';
import { FileUploadArea, UploadedDocument, UploadedFile, Button, StandardDialog, StatusDiv, FileUpload, PDFPreview } from '@roc/ui';
import { useStore } from '@roc/client-portal-shared/hooks';
import { DocReviewSection } from '../../docReviewSection';
import { PurchaseContractReviewTable } from './purchaseContractReviewTable';
import { PurchaseContractEditModal } from './purchaseContractEditModal';
import { emptyTableText } from '../../../utils/utils';
import { FieldReviewTable } from '../fieldReviewTable';

const useStyles = makeStyles((theme: Theme) => ({
  sectionLabel: {
    fontWeight: 700,
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
  },
  emptyContent: {
    padding: theme.spacing(3, 2),
    minHeight: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  relatedTaskRow: {
    display: 'flex',
    alignItems: 'center',
    borderTop: '1px solid #E0E0E0',
  },
  relatedTaskLabel: {
    flex: '0 0 40%',
    padding: theme.spacing(1.5, 2),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  relatedTaskValue: {
    flex: '1 1 auto',
    padding: theme.spacing(1.5, 2),
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  addendumQuestion: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
  },
  addendumLabel: {
    fontWeight: 700,
    color: '#000000'
  },
}));

const ADDENDUM_TYPE = 'ADDENDUM';

export interface PurchaseContractReviewDialogProps {
  open: boolean;
  handleClose: () => void;
  taskId: number;
}

export const PurchaseContractReviewDialog = observer(({
  open,
  handleClose,
  taskId,
}: PurchaseContractReviewDialogProps) => {
  const classes = useStyles();
  const { docReviewStoreV2, documentStore, propertyDocumentDataStore } = useStore();
  const isInternal = docReviewStoreV2.globalStore.isInternalPortal;
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [hasAddendum, setHasAddendum] = useState(false);
  const [addendumPreviewOpen, setAddendumPreviewOpen] = useState(false);
  const document = docReviewStoreV2.document;
  const uploadLocked = useIsUploadLocked(document?.status);
  const { propertyDocumentDataId, addendumFileName, propertyDocumentDataPreviewData } = propertyDocumentDataStore;

  useEffect(() => {
    if (!open || !taskId) return;
    docReviewStoreV2.getReviewTaskData(taskId);
  }, [open, taskId]);

  useEffect(() => {
    if (!open || !document?.loanId) return;
    propertyDocumentDataStore.fetch(document.loanId, document.collateralId, ADDENDUM_TYPE);
  }, [open, document?.loanId, document?.collateralId]);

  useEffect(() => {
    if (propertyDocumentDataId) setHasAddendum(true);
  }, [propertyDocumentDataId]);

  const onClose = () => {
    docReviewStoreV2.reset();
    setHasAddendum(false);
    propertyDocumentDataStore.reset();
    handleClose();
  };

  const handleReupload = async (file: File) => {
    await documentStore.uploadDocument(file, document, document.loanProcess);
    await docReviewStoreV2.getReviewTaskData(taskId);
    docReviewStoreV2.setProcessingStatus('IN_PROGRESS');
  };

  const handleSetFiles = (updater: FileUpload[] | ((prev: FileUpload[]) => FileUpload[])) => {
    const resolved = typeof updater === 'function' ? updater([]) : updater;
    const file = resolved[0]?.file;
    if (file) handleReupload(file);
  };

  const handleAddendumUpload = async (file: File) => {
    await propertyDocumentDataStore.upload(
      file,
      document?.loanId,
      document?.collateralId,
      taskId,
      ADDENDUM_TYPE
    );
    await docReviewStoreV2.getReviewTaskData(taskId);
  };

  const handleAddendumSetFiles = (updater: FileUpload[] | ((prev: FileUpload[]) => FileUpload[])) => {
    const resolved = typeof updater === 'function' ? updater([]) : updater;
    const file = resolved[0]?.file;
    if (file) handleAddendumUpload(file);
  };

  const handleAddendumView = () => {
    if (propertyDocumentDataId) {
      setAddendumPreviewOpen(true);
      propertyDocumentDataStore.preview(propertyDocumentDataId);
    }
  };

  const handleAddendumPreviewClose = () => {
    setAddendumPreviewOpen(false);
    propertyDocumentDataStore.clearPreview();
  };

  const handleSaveEdit = async () => {
    await docReviewStoreV2.saveDocReviewTaskData(document);
    setEditModalOpen(false);
  };

  const editDialogActions = (
    <Grid container justifyContent="flex-end" spacing={2}>
      <Grid item>
        <Button onClick={() => setEditModalOpen(false)} variant="outlined" color="primary" testId="cancel">
          Cancel
        </Button>
      </Grid>
      <Grid item>
        <Button onClick={handleSaveEdit} variant="contained" color="primary" testId="save">
          Save
        </Button>
      </Grid>
    </Grid>
  );

  const dialogContent = (
    <Grid container spacing={2}>
      {(document?.dropboxPath || !uploadLocked) && (
        <Grid item xs={12}>
          <Typography className={classes.sectionLabel}>
            Upload Contract
          </Typography>
          {document?.dropboxPath ? (
            <UploadedDocument
              document={document}
              onUpload={uploadLocked ? undefined : handleReupload}
              disabled={docReviewStoreV2.processingStatus === 'IN_PROGRESS'}
            />
          ) : (
            <FileUploadArea items={[]} setItems={handleSetFiles} />
          )}
        </Grid>
      )}
      <Grid item xs={12}>
        <FormControl component="fieldset" className={classes.addendumQuestion}>
          <FormLabel className={classes.addendumLabel} focused={false}>Do you have an addendum?</FormLabel>
          <RadioGroup
            row
            value={hasAddendum}
            onChange={e => setHasAddendum(e.target.value === 'true')}
          >
            <FormControlLabel value={true} control={<Radio />} label="Yes" disabled={!!propertyDocumentDataId || uploadLocked} />
            <FormControlLabel value={false} control={<Radio />} label="No" disabled={!!propertyDocumentDataId || uploadLocked} />
          </RadioGroup>
        </FormControl>
      </Grid>
      {hasAddendum && (propertyDocumentDataId || !uploadLocked) && (
        <Grid item xs={12}>
          <Typography className={classes.sectionLabel}>
            Upload Addendum
          </Typography>
          {propertyDocumentDataId ? (
            <UploadedFile
              fileName={addendumFileName}
              onUpload={uploadLocked ? undefined : handleAddendumUpload}
              onView={handleAddendumView}
              disabled={docReviewStoreV2.processingStatus === 'IN_PROGRESS'}
            />
          ) : (
            <FileUploadArea items={[]} setItems={handleAddendumSetFiles} />
          )}
        </Grid>
      )}
      {docReviewStoreV2.processingStatus &&
        (isInternal || docReviewStoreV2.processingStatus === 'IN_PROGRESS') && (
        <Grid item xs={12}>
          <StatusDiv
            status={docReviewStoreV2.processingStatus}
            text={docReviewStoreV2.statusText}
            onRefresh={isInternal ? () => docReviewStoreV2.getReviewTaskData(document.taskId) : undefined}
          />
        </Grid>
      )}
      {isInternal && (
        <Grid item xs={12}>
          <Typography className={classes.sectionLabel}>
            Contract Data
          </Typography>
          <DocReviewSection
            onEditClick={(docReviewStoreV2.processingStatus != 'IN_PROGRESS' &&
              document?.dropboxPath) ?
              () => setEditModalOpen(true) : undefined}
            editModal={
              document?.dropboxPath && (
                <StandardDialog
                  open={editModalOpen}
                  title={`Edit ${document.taskName ?? ''}`}
                  maxWidth="sm"
                  handleClose={() => setEditModalOpen(false)}
                  dialogContent={<PurchaseContractEditModal open={editModalOpen} />}
                  dialogActions={editDialogActions}
                />
              )
            }
            hideEdit={docReviewStoreV2.getIsEditDisabled(document?.taskName || '')}
          >
            {Object.keys(docReviewStoreV2.docReviewData).length > 0 ? (
              <PurchaseContractReviewTable
                data={docReviewStoreV2.docReviewData}
              />
            ) : (
              <Box className={classes.emptyContent}>
                <Typography className={classes.emptyText}>
                  {emptyTableText[docReviewStoreV2.processingStatus] ?? 'No data extracted yet. Upload a document to get started.'}
                </Typography>
              </Box>
            )}
          </DocReviewSection>
        </Grid>
      )}
      {isInternal && hasAddendum && (
        <Grid item xs={12}>
          <Typography className={classes.sectionLabel}>
            Addendum Data
          </Typography>
          <DocReviewSection>
            {docReviewStoreV2.docReviewData?.addendumItems?.length > 0 ? (
              <FieldReviewTable items={docReviewStoreV2.docReviewData.addendumItems} />
            ) : (
              <Box className={classes.emptyContent}>
                <Typography className={classes.emptyText}>
                  {emptyTableText.SUCCESS}
                </Typography>
              </Box>
            )}
          </DocReviewSection>
        </Grid>
      )}
    </Grid>
  );

  const dialogActions = (
    <Grid container justifyContent="flex-end">
      <Grid item>
        <Button
          onClick={onClose}
          color="primary"
          variant="outlined"
          testId="close"
        >
          Close
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <>
      {taskId && (
        <CustomTaskDialog
          open={open}
          maxWidth="md"
          title={document?.title}
          taskId={taskId}
          allowVertScroll={false}
          handleClose={onClose}
          dialogContent={dialogContent}
          dialogActions={dialogActions}
        />
      )}
      <PDFPreview
        open={addendumPreviewOpen}
        title={addendumFileName ?? 'Addendum'}
        fileName={addendumFileName}
        data={propertyDocumentDataPreviewData?.data}
        headers={propertyDocumentDataPreviewData?.headers}
        onClose={handleAddendumPreviewClose}
      />
    </>
  );
});
